import React, { createContext, useContext, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useThemeType } from '../../../hooks/useThemeType'
import { appendTransparency, createChartTheme } from '../chartColorRegistry'

const namedThemes = {
  default: ['blue', 'orange', 'indigo', 'yellow', 'purple', 'pink', 'green', 'red', 'cyan', 'lime'],
  primary: ['primary'],
  secondary: ['secondary'],
  accent: ['accent'],
  firm: ['primary', 'secondary', 'accent'],
  blue: ['blue'],
  red: ['red'],
  gray: ['gray'],
  indigo: ['indigo'],
  purple: ['purple'],
  pink: ['pink'],
  orange: ['orange'],
  yellow: ['yellow'],
  green: ['green'],
  lime: ['lime'],
  cyan: ['cyan'],
  orion: ['blue'],
  cool: ['blue', 'indigo', 'cyan', 'purple', 'green'],
  warm: ['pink', 'red', 'orange', 'yellow', 'lime']
}

const ChartColorContext = createContext(createChartTheme(namedThemes.default))

export const useChartColorContext = () => {
  return useContext(ChartColorContext)
}

export const useChartColors = (theme, ratioLow = 2, ratioHigh = 16, alpha = 1) => {
  const contextColors = useChartColorContext()
  const themeType = useThemeType()
  return useMemo(() => {
    if (!theme && contextColors?.length) return contextColors
    if (!theme && !contextColors?.length) return namedThemes.default

    const namedColors = (typeof theme === 'string')
      ? theme in namedThemes
        ? namedThemes[theme]
        : namedThemes.default
      : theme
    const colors = createChartTheme(namedColors, ratioLow, ratioHigh, themeType)
    return appendTransparency(colors, alpha)
  }, [contextColors, theme, alpha, themeType, ratioLow, ratioHigh])
}

function ChartColorProvider ({ theme, ratioLow, ratioHigh, alpha, children }) {
  const chartColors = useChartColors(theme, ratioLow, ratioHigh, alpha)

  return (
    <ChartColorContext.Provider value={chartColors}>
      {children}
    </ChartColorContext.Provider>
  )
}

ChartColorProvider.propTypes = {
  theme: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  ratioLow: PropTypes.number,
  ratioHigh: PropTypes.number,
  alpha: PropTypes.number,
  children: PropTypes.node
}

ChartColorProvider.defaultProps = {
  theme: 'default',
  ratioLow: 2,
  ratioHigh: 16,
  alpha: 1
}

export default ChartColorProvider
